<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">
      <q-icon name="img:/img/icons/icon_transporter_pressed.png" size="lg" color="primary" />
      Transporter Management
    </h5>

    <TransporterFilter
      :search.sync="search"
      :sort.sync="sort"
      :verification.sync="verification"
      :port.sync="port"
      @reset="handleReset"
      @search="handleSearch"
    />

    <LoadingTable v-if="isLoading" :columns="columns" />
    <DataTable v-else ref="table" :columns="columns">
      <tr v-if="!transporters.length">
        <td class="text-center" :colspan="columns.length">No records found.</td>
      </tr>

      <template v-else>
        <tr v-for="(transporter, idx) in transporters" :key="transporter.id">
          <td>
            <span class="text-action" @click="(editDialogOpen = true), (selectedTransporter = idx)">
              {{ transporter.id | formatId }}
            </span>
          </td>

          <td class="text-capitalize">{{ transporter.companyName }}</td>
          <td>
            <Avatar :src="transporter.companyLogo" />
            <span class="text-uppercase">{{ transporter.tradingName }}</span>
          </td>
          <td>{{ transporter.abn }}</td>
          <td>{{ transporter.user.email }}</td>
          <td class="text-capitalize">{{ transporter.repFirstName }} {{ transporter.repLastName }}</td>
          <td>{{ transporter.mainOfficePhone }}</td>
          <td>
            <span v-for="(address, idx2) in transporter.companyAddress" :key="idx2">
              {{ address }}
              <br />
            </span>
          </td>
          <td>{{ transporter.operationCity.name }}</td>

          <td>
            <span v-if="transporter.user.isActive === true" class="text-weight-medium text-positive"> Active </span>
            <span v-else class="text-weight-medium text-negative"> Suspended </span>
          </td>

          <td>
            <span class="text-weight-medium text-capitalize" :class="getStatusColor(transporter.status)">
              {{ transporter.status }}
            </span>
          </td>
          <td>{{ transporter.createdAt | formatDate }}</td>
          <td>
            <span class="text-action" @click="setSuspendModal(transporter)">
              <template v-if="transporter.user.isActive"> Suspend user </template>
              <template v-else> Enable user </template>
            </span>
          </td>
        </tr>
      </template>
    </DataTable>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <EditUserDialog
      v-if="editDialogOpen"
      :open.sync="editDialogOpen"
      :selected-transporter="editDialogTransporter"
      @suspendUser="setSuspendModal(editDialogTransporter)"
      @enableUser="setSuspendModal(editDialogTransporter)"
      @checkTransporterChange="$event ? debouncedSendRequest() : ''"
    />

    <EditTransporterStatus
      :is-open.sync="suspendTransporterModal.isOpen"
      :transporter="suspendTransporterModal.user"
      :on-transporter-status-update="updateTransporterStatus"
    />
  </Wrapper>
</template>

<script>
import _debounce from 'lodash/debounce';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTableNew.vue';
import LoadingTable from '@/components/LoadingTable.vue';
import Pagination from '@/components/Pagination.vue';
import Avatar from '@/components/Avatar.vue';
import { getErrorMessage } from '@/utils';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import API from '@/apis';
import TransporterFilter from './Transporter/TransporterFilter.vue';
import EditUserDialog from './Transporter/EditUserDialog.vue';
import EditTransporterStatus from './Transporter/EditTransporterStatus.vue';

const userModalState = {
  isOpen: false,
  user: {},
};

export default {
  name: 'AdminTransporterManagement',

  components: {
    Avatar,
    LoadingTable,
    Wrapper,
    DataTable,
    TransporterFilter,
    Pagination,
    EditUserDialog,
    EditTransporterStatus,
  },

  mixins: [formatDateMixin],

  data() {
    return {
      isLoading: true,
      actionLoading: false,
      transporters: [],
      search: '',
      sort: '',
      verification: '',
      port: '',
      currentPage: 1,
      totalPages: 1,
      suspendTransporterModal: { ...userModalState },
      editDialogOpen: false,
      selectedTransporter: null,
      columns: [
        'ID',
        'Company Name',
        'Trading Name',
        'ABN',
        'Email',
        'Representative Name',
        'Main Office Phone',
        'Company Address',
        'Operation Port',
        'Status',
        'Verification Status',
        'Registration Date',
        'Action',
      ],
    };
  },

  computed: {
    editDialogTransporter() {
      return this.transporters[this.selectedTransporter];
    },
  },

  watch: {
    currentPage() {
      this.debouncedSendRequest();
      window.scrollTo({ top: this.$refs.table.$el.offsetTop, behavior: 'smooth' });
    },
  },

  created() {
    this.debouncedSendRequest = _debounce(this.sendRequest, 500);
    this.sendRequest();
  },

  methods: {
    getStatusColor(transporterStatus) {
      switch (transporterStatus) {
        case 'pending':
          return 'text-warning';
        case 'verified':
          return 'text-positive';
        case 'unverified':
          return 'text-muted';
        default:
          throw new Error('unkown transporter status');
      }
    },
    handleSearch() {
      if (this.currentPage > 1) {
        // Reset page on search
        // new page value will trigger watch
        this.currentPage = 1;
        this.totalPages = 1;
      } else {
        this.debouncedSendRequest();
      }
    },
    async handleReset() {
      // Do nothing at initial state
      if (
        this.currentPage === 1 &&
        this.totalPages === 1 &&
        !this.search &&
        !this.sort &&
        !this.verification &&
        !this.port
      ) {
        return;
      }

      if (this.currentPage === 1) {
        await this.debouncedSendRequest();
      }

      this.search = '';
      this.sort = '';
      this.verification = '';
      this.port = '';
      // new page value will trigger watch
      this.currentPage = 1;
      this.totalPages = 1;
    },
    getParams() {
      const params = {};

      if (this.search) {
        params.search = this.search;
      }

      if (this.sort) {
        params.sort = this.sort;
      }

      if (this.verification) {
        params.verification = this.verification;
      }

      if (this.port) {
        params.port = this.port;
      }

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      return params;
    },
    async sendRequest() {
      this.isLoading = true;

      try {
        const params = this.getParams();
        const { data } = await this.$axios.request(API.admin.getTransporters({ params }));

        this.transporters = data.data;
        this.currentPage = data.pagination.page;
        this.totalPages = data.pagination.pages;
      } catch (error) {
        this.$q.notify({
          message: getErrorMessage(error),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handler: this.sendRequest }],
        });
      } finally {
        this.isLoading = false;
      }
    },
    debouncedSendRequest() {},
    async updateTransporterStatus(id) {
      this.suspendTransporterModal = { ...userModalState };

      const userIndex = this.transporters.findIndex((u) => u.id === id);

      this.transporters[userIndex].user.isActive = !this.transporters[userIndex].user.isActive;
    },
    setSuspendModal(user) {
      this.suspendTransporterModal.isOpen = true;
      this.suspendTransporterModal.user = user;
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-textarea {
  width: 550px;
  height: 15vw;

  @media screen and (min-width: 64em) {
    width: 700px;
  }

  @media screen and (min-width: 75em) {
    width: 900px;
  }
}
</style>
